(function(window, document){
  'use strict';

  if(window.MoonasStaff3D) return;

  var TAU = Math.PI * 2;
  var COPPER = 0xB06A33;
  var GOLD = 0xC2902A;
  var PAPER = 0xFAF6EC;
  var TEAL = 0x0D98BA;

  function clamp(value, min, max){
    return Math.max(min, Math.min(max, value));
  }

  function disposeMaterial(material){
    if(!material) return;
    Object.keys(material).forEach(function(key){
      var value = material[key];
      if(value && value.isTexture && value.dispose) value.dispose();
    });
    if(material.dispose) material.dispose();
  }

  function disposeObject(object){
    if(!object) return;
    object.traverse(function(child){
      if(child.geometry && child.geometry.dispose) child.geometry.dispose();
      if(Array.isArray(child.material)) child.material.forEach(disposeMaterial);
      else if(child.material) disposeMaterial(child.material);
    });
    if(object.parent) object.parent.remove(object);
  }

  function removeChildren(group){
    while(group.children.length) disposeObject(group.children[0]);
  }

  function gaugeRadius(mm, minRadius, maxRadius){
    var normal = clamp((Number(mm || 2.05) - 1.29) / (4.12 - 1.29), 0, 1);
    return minRadius + (maxRadius - minRadius) * normal;
  }

  function StaffScene(canvas, opts){
    if(!canvas || !canvas.getContext) throw new Error('A canvas element is required.');
    if(!window.THREE) throw new Error('Three.js is not available.');

    this.canvas = canvas;
    this.opts = opts || {};
    this.THREE = window.THREE;
    this.disposed = false;
    this.failed = false;
    this.visible = true;
    this.pageVisible = !document.hidden;
    this.frameId = 0;
    this.lastFrame = 0;
    this.lastInput = Date.now();
    this.targetProgress = 0;
    this.currentProgress = 0;
    this.idleRotation = 0;
    this.dragRotation = 0;
    this.targetDragRotation = 0;
    this.dragging = false;
    this.pointerStartX = 0;
    this.pointerStartRotation = 0;
    this.headType = 'Standard';
    this.gaugeMm = 2.05;
    this.acuvacForm = 'Single';
    this.acuvacGaugeMm = 2.05;
    this.crystalColor = '#cfe9f2';
    this.staffCrystalColors = [];
    this.mobileDetail = window.innerWidth < 860;
    this.reducedMotion = !!this.opts.reducedMotion;

    this._frame = this._frame.bind(this);
    this._resize = this._resize.bind(this);
    this._onVisibility = this._onVisibility.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);

    try {
      this._build();
      this._resize();
      this.renderer.render(this.scene, this.camera);
      this._connectLifecycle();
      this._requestFrame();
    } catch(error){
      this.dispose();
      throw error;
    }
  }

  StaffScene.prototype._build = function(){
    var THREE = this.THREE;
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance'
    });
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    this.renderer.setClearColor(PAPER, 0);
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.92;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(34, 1, 0.1, 40);
    this.camera.position.set(0, 2.15, 6.5);
    this.cameraLook = new THREE.Vector3(0, 2.15, 0);
    this.camera.lookAt(this.cameraLook);

    this.objectRoot = new THREE.Group();
    this.objectRoot.rotation.x = -0.035;
    this.scene.add(this.objectRoot);

    this.crownGroup = new THREE.Group();
    this.crownGroup.position.y = 2.2;
    this.objectRoot.add(this.crownGroup);

    this.crystalGroup = new THREE.Group();
    this.crystalGroup.position.y = 2.2;
    this.objectRoot.add(this.crystalGroup);

    this.staffGroup = new THREE.Group();
    this.objectRoot.add(this.staffGroup);

    this.acuvacGroup = new THREE.Group();
    this.objectRoot.add(this.acuvacGroup);

    this.staffCrystalsGroup = new THREE.Group();
    this.objectRoot.add(this.staffCrystalsGroup);

    this._buildEnvironment();
    this._buildLights();
    this._buildStaff();
    this._buildCrown();
    this._buildCentreCrystal();
    this._buildAcuvac();
    this._buildStaffCrystals();
  };

  StaffScene.prototype._buildEnvironment = function(){
    var THREE = this.THREE;
    var envCanvas = document.createElement('canvas');
    envCanvas.width = 128;
    envCanvas.height = 64;
    var ctx = envCanvas.getContext('2d');
    var gradient = ctx.createLinearGradient(0, 0, 0, envCanvas.height);
    gradient.addColorStop(0, '#fff5d9');
    gradient.addColorStop(0.45, '#c99a63');
    gradient.addColorStop(0.72, '#6e8d91');
    gradient.addColorStop(1, '#211a15');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, envCanvas.width, envCanvas.height);

    var glow = ctx.createRadialGradient(34, 18, 2, 34, 18, 32);
    glow.addColorStop(0, 'rgba(255,255,255,.95)');
    glow.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, envCanvas.width, envCanvas.height);

    this.environmentTexture = new THREE.CanvasTexture(envCanvas);
    this.environmentTexture.mapping = THREE.EquirectangularReflectionMapping;
    this.environmentTexture.encoding = THREE.sRGBEncoding;
    this.pmrem = new THREE.PMREMGenerator(this.renderer);
    this.pmrem.compileEquirectangularShader();
    this.environmentTarget = this.pmrem.fromEquirectangular(this.environmentTexture);
    this.scene.environment = this.environmentTarget.texture;
    this.environmentTexture.dispose();
    this.environmentTexture = null;
    this.pmrem.dispose();
    this.pmrem = null;
  };

  StaffScene.prototype._buildLights = function(){
    var THREE = this.THREE;
    var hemi = new THREE.HemisphereLight(0xfff5dc, 0x335d65, 1.0);
    this.scene.add(hemi);
    var key = new THREE.DirectionalLight(0xffdfad, 2.25);
    key.position.set(4.5, 6.5, 5);
    this.scene.add(key);
    var rim = new THREE.DirectionalLight(TEAL, 0.85);
    rim.position.set(-5, 1.5, -4);
    this.scene.add(rim);
    var fill = new THREE.PointLight(0xfff7e9, 0.8, 16);
    fill.position.set(-2, 0.5, 4);
    this.scene.add(fill);
  };

  StaffScene.prototype._copperMaterial = function(roughness){
    return new this.THREE.MeshStandardMaterial({
      color: COPPER,
      metalness: 1,
      roughness: roughness == null ? 0.32 : roughness,
      envMapIntensity: 1.35
    });
  };

  StaffScene.prototype._goldMaterial = function(){
    return new this.THREE.MeshStandardMaterial({
      color: GOLD,
      metalness: 1,
      roughness: 0.28,
      transparent: true,
      opacity: 0.82,
      envMapIntensity: 1.45
    });
  };

  StaffScene.prototype._addCoilColors = function(geometry){
    var THREE = this.THREE;
    var positions = geometry.getAttribute('position');
    var colors = new Float32Array(positions.count * 3);
    var copper = new THREE.Color(COPPER);
    var gold = new THREE.Color(GOLD);
    var mixed = new THREE.Color();
    for(var i = 0; i < positions.count; i++){
      var blend = 0.08 + 0.28 * (0.5 + 0.5 * Math.sin(i * 0.071));
      mixed.copy(copper).lerp(gold, blend);
      colors[i * 3] = mixed.r;
      colors[i * 3 + 1] = mixed.g;
      colors[i * 3 + 2] = mixed.b;
    }
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  };

  StaffScene.prototype._coilMaterial = function(){
    return new this.THREE.MeshStandardMaterial({
      color: 0xffffff,
      vertexColors: true,
      metalness: 1,
      roughness: 0.32,
      envMapIntensity: 1.55
    });
  };

  StaffScene.prototype._toroidalCurve = function(turns, majorRadius, minorRadius, phase, flatten){
    var THREE = this.THREE;
    class ToroidalCurve extends THREE.Curve {
      getPoint(t, target){
        var theta = TAU * t;
        var phi = TAU * turns * t + phase;
        var ring = majorRadius + minorRadius * Math.cos(phi);
        var result = target || new THREE.Vector3();
        return result.set(
          ring * Math.cos(theta),
          minorRadius * Math.sin(phi) * flatten,
          ring * Math.sin(theta)
        );
      }
    }
    return new ToroidalCurve();
  };

  StaffScene.prototype._helixCurve = function(turns){
    var THREE = this.THREE;
    class HelixCurve extends THREE.Curve {
      getPoint(t, target){
        var angle = TAU * turns * t;
        var radius = 0.31 + Math.sin(t * Math.PI) * 0.025;
        var result = target || new THREE.Vector3();
        return result.set(
          Math.cos(angle) * radius,
          1.72 - t * 4.69,
          Math.sin(angle) * radius
        );
      }
    }
    return new HelixCurve();
  };

  StaffScene.prototype._buildCrown = function(){
    var THREE = this.THREE;
    removeChildren(this.crownGroup);

    var config = {
      Standard: {turns: 28, major: 1.43, minor: 0.29, flatten: 1, doubleWind: false, gaugeScale: 1},
      Matrix: {turns: 34, major: 1.42, minor: 0.31, flatten: 1, doubleWind: true, gaugeScale: 0.78},
      'Earth Core': {turns: 23, major: 1.52, minor: 0.3, flatten: 0.62, doubleWind: false, gaugeScale: 1.3},
      Vogel: {turns: 19, major: 1.42, minor: 0.28, flatten: 1, doubleWind: false, gaugeScale: 1.04},
      Custom: {turns: 28, major: 1.43, minor: 0.29, flatten: 1, doubleWind: false, gaugeScale: 1}
    };
    var c = config[this.headType] || config.Standard;
    var tubeRadius = gaugeRadius(this.gaugeMm, 0.026, 0.058) * c.gaugeScale;
    var tubular = this.mobileDetail ? 230 : 420;
    var radial = this.mobileDetail ? 5 : 7;
    var curve = this._toroidalCurve(c.turns, c.major, c.minor, 0, c.flatten);
    var geometry = new THREE.TubeGeometry(curve, tubular, tubeRadius, radial, false);
    this._addCoilColors(geometry);
    this.crownGroup.add(new THREE.Mesh(geometry, this._coilMaterial()));

    if(c.doubleWind){
      var curveTwo = this._toroidalCurve(c.turns, c.major, c.minor, Math.PI, c.flatten);
      var geometryTwo = new THREE.TubeGeometry(curveTwo, tubular, tubeRadius * 0.88, radial, false);
      this._addCoilColors(geometryTwo);
      this.crownGroup.add(new THREE.Mesh(geometryTwo, this._coilMaterial()));
    }

    var guideMaterial = this._goldMaterial();
    var ringOne = new THREE.Mesh(new THREE.TorusGeometry(c.major, 0.012, 5, this.mobileDetail ? 72 : 120), guideMaterial);
    ringOne.rotation.x = Math.PI / 2;
    ringOne.scale.y = c.flatten === 1 ? 1 : 0.9;
    this.crownGroup.add(ringOne);
    var ringTwo = new THREE.Mesh(new THREE.TorusGeometry(c.major + c.minor + 0.07, 0.008, 4, this.mobileDetail ? 64 : 100), this._goldMaterial());
    ringTwo.rotation.x = Math.PI / 2;
    ringTwo.material.opacity = 0.34;
    this.crownGroup.add(ringTwo);
  };

  StaffScene.prototype._buildCentreCrystal = function(){
    var THREE = this.THREE;
    removeChildren(this.crystalGroup);
    var color = new THREE.Color(this.crystalColor);
    var material = new THREE.MeshPhysicalMaterial({
      color: color,
      emissive: color.clone().multiplyScalar(0.38),
      emissiveIntensity: 0.85,
      metalness: 0.02,
      roughness: 0.16,
      transmission: 0.22,
      ior: 1.6,
      transparent: true,
      opacity: 0.98,
      clearcoat: 0.75,
      clearcoatRoughness: 0.12,
      envMapIntensity: 1.5,
      side: THREE.DoubleSide
    });
    var crystal = new THREE.Mesh(new THREE.OctahedronGeometry(0.34, 0), material);
    if(this.headType === 'Vogel') crystal.scale.set(1.35, 2.3, 1.35);
    else crystal.scale.set(0.95, 1.75, 0.95);
    crystal.rotation.y = Math.PI / 4;
    this.crystalGroup.add(crystal);

    var collarRadius = this.headType === 'Vogel' ? 0.46 : 0.34;
    var collar = new THREE.Mesh(new THREE.TorusGeometry(collarRadius, 0.022, 6, 48), this._goldMaterial());
    collar.rotation.x = Math.PI / 2;
    this.crystalGroup.add(collar);
  };

  StaffScene.prototype._buildStaff = function(){
    var THREE = this.THREE;
    removeChildren(this.staffGroup);
    var rodMaterial = this._copperMaterial(0.37);
    var centralMaterial = this._goldMaterial();
    var rodGeometryOne = new THREE.CylinderGeometry(0.052, 0.052, 5.25, this.mobileDetail ? 8 : 12);
    var left = new THREE.Mesh(rodGeometryOne, rodMaterial);
    left.position.set(-0.19, -0.52, 0);
    this.staffGroup.add(left);
    var right = new THREE.Mesh(new THREE.CylinderGeometry(0.052, 0.052, 5.25, this.mobileDetail ? 8 : 12), this._copperMaterial(0.37));
    right.position.set(0.19, -0.52, 0);
    this.staffGroup.add(right);
    var central = new THREE.Mesh(new THREE.CylinderGeometry(0.057, 0.062, 5.46, this.mobileDetail ? 8 : 12), centralMaterial);
    central.position.set(0, -0.5, 0.08);
    this.staffGroup.add(central);

    var neck = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.14, 0.32, this.mobileDetail ? 12 : 18), this._copperMaterial(0.3));
    neck.position.y = 2.08;
    this.staffGroup.add(neck);
    var foot = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.34, 0.28, this.mobileDetail ? 12 : 20), this._copperMaterial(0.3));
    foot.position.y = -3.13;
    this.staffGroup.add(foot);
    var footRing = new THREE.Mesh(new THREE.TorusGeometry(0.27, 0.035, 7, 36), this._goldMaterial());
    footRing.position.y = -3.02;
    footRing.rotation.x = Math.PI / 2;
    this.staffGroup.add(footRing);
  };

  StaffScene.prototype._buildAcuvac = function(){
    var THREE = this.THREE;
    removeChildren(this.acuvacGroup);
    var turnsByForm = {Single: 14, Triple: 27, 'Dragon-Scale': 41};
    var turns = turnsByForm[this.acuvacForm] || turnsByForm.Single;
    var tubeRadius = gaugeRadius(this.acuvacGaugeMm, 0.018, 0.043);
    if(this.acuvacForm === 'Dragon-Scale') tubeRadius *= 1.08;
    var tubular = this.mobileDetail ? 190 : 360;
    var geometry = new THREE.TubeGeometry(this._helixCurve(turns), tubular, tubeRadius, this.mobileDetail ? 5 : 7, false);
    this._addCoilColors(geometry);
    this.acuvacGroup.add(new THREE.Mesh(geometry, this._coilMaterial()));
  };

  StaffScene.prototype._buildStaffCrystals = function(){
    var THREE = this.THREE;
    removeChildren(this.staffCrystalsGroup);
    var nodes = [1.42, 0.62, -0.18, -0.98, -1.78, -2.53];
    for(var i = 0; i < this.staffCrystalColors.length && i < nodes.length; i++){
      var color = new THREE.Color(this.staffCrystalColors[i]);
      var material = new THREE.MeshPhysicalMaterial({
        color: color,
        emissive: color.clone().multiplyScalar(0.34),
        emissiveIntensity: 0.78,
        roughness: 0.2,
        metalness: 0.02,
        transmission: 0.28,
        transparent: true,
        opacity: 0.9,
        clearcoat: 0.65,
        envMapIntensity: 1.35
      });
      var crystal = new THREE.Mesh(new THREE.OctahedronGeometry(0.16, 0), material);
      crystal.position.set(i % 2 ? 0.25 : -0.25, nodes[i], i % 3 === 0 ? 0.08 : -0.04);
      crystal.scale.y = 1.4;
      crystal.rotation.y = i * 0.72;
      this.staffCrystalsGroup.add(crystal);
      var seat = new THREE.Mesh(new THREE.TorusGeometry(0.115, 0.018, 5, 24), this._goldMaterial());
      seat.position.copy(crystal.position);
      seat.rotation.x = Math.PI / 2;
      this.staffCrystalsGroup.add(seat);
    }
  };

  StaffScene.prototype._connectLifecycle = function(){
    var self = this;
    if('ResizeObserver' in window){
      this.resizeObserver = new ResizeObserver(function(){ self._resize(); });
      this.resizeObserver.observe(this.canvas);
    } else {
      window.addEventListener('resize', this._resize);
    }

    if('IntersectionObserver' in window){
      this.visibilityObserver = new IntersectionObserver(function(entries){
        entries.forEach(function(entry){
          self.visible = entry.isIntersecting;
          if(self.visible) self._requestFrame();
          else self._cancelFrame();
        });
      }, {threshold: 0.01, rootMargin: '120px 0px'});
      this.visibilityObserver.observe(this.canvas);
    }

    document.addEventListener('visibilitychange', this._onVisibility);
    if(!this.reducedMotion){
      this.canvas.style.touchAction = 'pan-y';
      this.canvas.addEventListener('pointerdown', this._onPointerDown);
      window.addEventListener('pointermove', this._onPointerMove, {passive: true});
      window.addEventListener('pointerup', this._onPointerUp, {passive: true});
      window.addEventListener('pointercancel', this._onPointerUp, {passive: true});
    }
  };

  StaffScene.prototype._onVisibility = function(){
    this.pageVisible = !document.hidden;
    if(this.pageVisible && this.visible) this._requestFrame();
    else this._cancelFrame();
  };

  StaffScene.prototype._onPointerDown = function(event){
    if(event.pointerType === 'mouse' && event.button !== 0) return;
    this.dragging = true;
    this.pointerStartX = event.clientX;
    this.pointerStartRotation = this.targetDragRotation;
    this.lastInput = Date.now();
    if(this.canvas.setPointerCapture){
      try { this.canvas.setPointerCapture(event.pointerId); } catch(ignore) {}
    }
  };

  StaffScene.prototype._onPointerMove = function(event){
    if(!this.dragging) return;
    this.targetDragRotation = clamp(this.pointerStartRotation + (event.clientX - this.pointerStartX) * 0.006, -0.68, 0.68);
    this.lastInput = Date.now();
  };

  StaffScene.prototype._onPointerUp = function(){
    this.dragging = false;
    this.lastInput = Date.now();
  };

  StaffScene.prototype._resize = function(){
    if(this.disposed || !this.renderer) return;
    var width = Math.max(1, this.canvas.clientWidth || this.canvas.parentNode.clientWidth || 1);
    var height = Math.max(1, this.canvas.clientHeight || this.canvas.parentNode.clientHeight || 1);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    if(this.visible && this.pageVisible) this._requestFrame();
  };

  StaffScene.prototype._requestFrame = function(){
    if(this.disposed || this.failed || this.frameId || !this.visible || !this.pageVisible) return;
    this.frameId = window.requestAnimationFrame(this._frame);
  };

  StaffScene.prototype._cancelFrame = function(){
    if(this.frameId){
      window.cancelAnimationFrame(this.frameId);
      this.frameId = 0;
    }
  };

  StaffScene.prototype._frame = function(time){
    this.frameId = 0;
    if(this.disposed || this.failed || !this.visible || !this.pageVisible) return;
    try {
      var delta = this.lastFrame ? Math.min(50, time - this.lastFrame) : 16;
      this.lastFrame = time;
      this.currentProgress += (this.targetProgress - this.currentProgress) * 0.065;
      this.dragRotation += (this.targetDragRotation - this.dragRotation) * 0.08;
      if(!this.dragging) this.targetDragRotation *= 0.982;

      if(!this.reducedMotion && Date.now() - this.lastInput > 1100) this.idleRotation += delta * 0.000035;
      var spin = this.reducedMotion ? 0 : this.currentProgress * TAU * 0.9;
      this.objectRoot.rotation.y = spin + this.dragRotation + this.idleRotation;

      var eased = this.currentProgress * this.currentProgress * (3 - 2 * this.currentProgress);
      var cameraY = 2.18 + (-0.28 - 2.18) * eased;
      var cameraZ = 6.35 + (9.2 - 6.35) * eased;
      var lookY = 2.18 + (-0.32 - 2.18) * eased;
      this.camera.position.y += (cameraY - this.camera.position.y) * 0.065;
      this.camera.position.z += (cameraZ - this.camera.position.z) * 0.065;
      this.cameraLook.y += (lookY - this.cameraLook.y) * 0.065;
      this.camera.lookAt(this.cameraLook);

      this.renderer.render(this.scene, this.camera);
      this._requestFrame();
    } catch(error){
      this._fail(error);
    }
  };

  StaffScene.prototype._fail = function(error){
    if(this.failed || this.disposed) return;
    this.failed = true;
    var onError = this.opts && this.opts.onError;
    this.dispose();
    if(typeof onError === 'function') onError(error);
  };

  StaffScene.prototype.setHead = function(typeString){
    if(this.disposed) return;
    var clean = String(typeString || 'Standard').replace(/\s+Harmonizer$/i, '');
    this.headType = clean || 'Standard';
    this._buildCrown();
    this._buildCentreCrystal();
  };

  StaffScene.prototype.setGauge = function(mm){
    if(this.disposed) return;
    this.gaugeMm = Number(mm) || 2.05;
    this._buildCrown();
  };

  StaffScene.prototype.setCentreCrystal = function(hexColor){
    if(this.disposed) return;
    this.crystalColor = hexColor || '#cfe9f2';
    this._buildCentreCrystal();
  };

  StaffScene.prototype.setAcuvac = function(formString, gaugeMm){
    if(this.disposed) return;
    this.acuvacForm = String(formString || 'Single').replace(/\s+Acu-Vac$/i, '');
    this.acuvacGaugeMm = Number(gaugeMm) || 2.05;
    this._buildAcuvac();
  };

  StaffScene.prototype.setStaffCrystals = function(hexColors){
    if(this.disposed) return;
    this.staffCrystalColors = Array.isArray(hexColors) ? hexColors.slice(0, 6) : [];
    this._buildStaffCrystals();
  };

  StaffScene.prototype.setScrollProgress = function(progress){
    if(this.disposed || this.reducedMotion) return;
    this.targetProgress = clamp(Number(progress) || 0, 0, 1);
    this.lastInput = Date.now();
    this._requestFrame();
  };

  StaffScene.prototype.dispose = function(){
    if(this.disposed) return;
    this.disposed = true;
    this._cancelFrame();
    document.removeEventListener('visibilitychange', this._onVisibility);
    window.removeEventListener('resize', this._resize);
    this.canvas.removeEventListener('pointerdown', this._onPointerDown);
    window.removeEventListener('pointermove', this._onPointerMove);
    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerUp);
    if(this.resizeObserver) this.resizeObserver.disconnect();
    if(this.visibilityObserver) this.visibilityObserver.disconnect();
    if(this.scene){
      this.scene.traverse(function(child){
        if(child.geometry && child.geometry.dispose) child.geometry.dispose();
        if(Array.isArray(child.material)) child.material.forEach(disposeMaterial);
        else if(child.material) disposeMaterial(child.material);
      });
    }
    if(this.environmentTarget) this.environmentTarget.dispose();
    if(this.environmentTexture) this.environmentTexture.dispose();
    if(this.pmrem) this.pmrem.dispose();
    if(this.renderer){
      this.renderer.dispose();
      if(this.renderer.forceContextLoss) this.renderer.forceContextLoss();
    }
    this.scene = null;
    this.renderer = null;
  };

  window.MoonasStaff3D = {
    init: function(canvasEl, opts){
      return new StaffScene(canvasEl, opts || {});
    }
  };
})(window, document);
